/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { AxiosResponse, RawAxiosRequestHeaders } from 'axios';
import { ProfileResponse } from '../../../models/LifeProfileResponse';
import { CoreNetworking, type ApiClient } from '@airasia-rn/ui/core-networking';
import { validateAccountResponse } from '../../../schema';

export class LifeApiService {
  private readonly client: ApiClient;

  constructor(baseURL: string) {
    this.client = CoreNetworking({
      baseUrl: baseURL,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  private async get<T>(
    url: string,
    params?: unknown,
    headers?: RawAxiosRequestHeaders,
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.get(url, {
        params,
        headers,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * GET /profile/v4/{userId}
   * Fetches the life profile for the given user.
   * API spec: Authorization: Bearer {token}
   */
  public async getProfile(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
  ): Promise<ProfileResponse> {
    const bearerToken = accessToken.startsWith('Bearer ')
      ? accessToken
      : `Bearer ${accessToken}`;

    const url = `/profile/v4/${userId}`;
    const response = await this.get(url, undefined, {
      'X-Api-Key': apiKey,
      'X-Aa-Client-Id': clientId,
      Authorization: bearerToken,
    });

    return validateAccountResponse(
      'lifeProfile',
      { method: 'GET', url },
      response as ProfileResponse,
    );
  }
}
